import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { useModules } from "@/lib/modules";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import QuickLogin from "@/pages/QuickLogin";
import DemoEntry from "@/pages/DemoEntry";
import Activate from "@/pages/Activate";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SuperAdmin from "@/pages/SuperAdmin";
import Watchtower from "@/pages/Watchtower";
import CustomerIdentityAdmin from "@/pages/CustomerIdentityAdmin";
import IndexMaintenance from "@/pages/IndexMaintenance";
import LegacyPage from "@/pages/Legacy";
import Dashboard from "@/pages/Dashboard";
import POS from "@/pages/POS";
import Inventory from "@/pages/Inventory";
import Suppliers from "@/pages/Suppliers";
import HardwareLibrary from "@/pages/HardwareLibrary";
import Customers from "@/pages/Customers";
import CustomerProfile from "@/pages/CustomerProfile";
import ARAging from "@/pages/ARAging";
import Payments from "@/pages/Payments";
import Reminders from "@/pages/Reminders";
import Expenses from "@/pages/Expenses";
import Reports from "@/pages/Reports";
import CustomReportPrintView from "@/pages/CustomReportPrintView";
import WiPaySettings from "@/pages/WiPaySettings";
import WiPayReconciliation from "@/pages/WiPayReconciliation";
import ShiftReport from "@/pages/ShiftReport";
import Quotations from "@/pages/Quotations";
import SalesHistory from "@/pages/SalesHistory";
import OfflineSync from "@/pages/OfflineSync";
import Pickups from "@/pages/Pickups";
import AuditLog from "@/pages/AuditLog";
import ApprovalsReport from "@/pages/ApprovalsReport";
import Staff from "@/pages/Staff";
import Settings from "@/pages/Settings";
import Security from "@/pages/Security";
import Billing from "@/pages/Billing";
import Help from "@/pages/Help";
import Promotions from "@/pages/Promotions";
import ImportData from "@/pages/ImportData";
import AccessRestricted from "@/components/AccessRestricted";
import BillingSuspended from "@/components/BillingSuspended";

function Protected({ children, roles, module, feature, historyAware = false, managerReportsGate = false }) {
  const { user, business, loading } = useAuth();
  const modules = useModules();
  if (loading) return <div className="h-screen flex items-center justify-center text-muted-foreground">Loading NEEC POS…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "superadmin") return <Navigate to="/super-admin" replace />;
  // Client-side guard: show a clean Access Restricted screen (never crash / redirect
  // silently). The backend still enforces every permission + feature server-side.
  if (roles && !roles.includes(user.role)) return <Layout><AccessRestricted module={module} reason="role" /></Layout>;
  // Owner Settings -> Permissions -> "Manager Access to Reports" (reports.access). Owner always
  // passes; this only ever gates the Manager role. Backend is the real enforcer (every report
  // route 403s a Manager when this is off) — this is just the clean UX for the common case of
  // navigating here fresh with the permission already off.
  if (managerReportsGate && user.role === "manager" && business?.managers_can_access_reports === false)
    return <Layout><AccessRestricted module={module} reason="manager_reports_off" /></Layout>;
  if (business?.suspended_for_billing && !["/billing", "/help"].includes(window.location.pathname))
    return <Layout><BillingSuspended /></Layout>;
  // Module gate (UX only; backend enforces). Kept separate from the role check above.
  // historyAware (Suppliers Phase 2): a module that is OFF but has operational history stays readable (read-only surfaces).
  // The reason (optional module OFF / Core Standard awaiting Owner activation / Core Standard turned off) drives the wording.
  if (feature && modules.isExplicitlyOff(feature) && !(historyAware && modules.hasHistory(feature)))
    return <Layout><AccessRestricted module={module || modules.moduleName(feature)} reason={modules.unavailableReason(feature) || "module_off"} /></Layout>;
  return <Layout>{children}</Layout>;
}

function SuperAdminRoute({ page }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "superadmin") return <Navigate to="/" replace />;
  if (page === "legacy") return <LegacyPage />;
  if (page === "watchtower") return <Watchtower />;
  if (page === "customer-identity") return <CustomerIdentityAdmin />;
  if (page === "index-maintenance") return <IndexMaintenance />;
  return <SuperAdmin />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/quick" element={<QuickLogin />} />
      <Route path="/demo" element={<DemoEntry />} />
      <Route path="/activate" element={<Activate />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/super-admin" element={<SuperAdminRoute />} />
      <Route path="/super-admin/legacy" element={<SuperAdminRoute page="legacy" />} />
      <Route path="/super-admin/watchtower" element={<SuperAdminRoute page="watchtower" />} />
      <Route path="/super-admin/customer-identity" element={<SuperAdminRoute page="customer-identity" />} />
      <Route path="/super-admin/index-maintenance" element={<SuperAdminRoute page="index-maintenance" />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/pos" element={<Protected><POS /></Protected>} />
      <Route path="/inventory" element={<Protected roles={["owner", "manager"]} module="Inventory"><Inventory /></Protected>} />
      <Route path="/suppliers" element={<Protected roles={["owner", "manager"]} module="Suppliers" feature="suppliers" historyAware><Suppliers /></Protected>} />
      <Route path="/suppliers/purchase-orders/:poId" element={<Protected roles={["owner", "manager"]} module="Suppliers" feature="suppliers" historyAware><Suppliers /></Protected>} />
      <Route path="/suppliers/receipts/:receiptId" element={<Protected roles={["owner", "manager"]} module="Suppliers" feature="suppliers" historyAware><Suppliers /></Protected>} />
      <Route path="/suppliers/:id" element={<Protected roles={["owner", "manager"]} module="Suppliers" feature="suppliers"><Suppliers /></Protected>} />
      <Route path="/library" element={<Protected roles={["owner", "manager"]} module="Hardware Library" feature="hardware_library"><HardwareLibrary /></Protected>} />
      <Route path="/customers" element={<Protected><Customers /></Protected>} />
      <Route path="/customers/:id" element={<Protected><CustomerProfile /></Protected>} />
      <Route path="/ar-aging" element={<Protected roles={["owner", "manager"]}><ARAging /></Protected>} />
      <Route path="/payments" element={<Protected><Payments /></Protected>} />
      <Route path="/reminders" element={<Protected module="Reminders" feature="reminders"><Reminders /></Protected>} />
      <Route path="/expenses" element={<Protected roles={["owner", "manager"]} module="Expenses" feature="expenses"><Expenses /></Protected>} />
      <Route path="/reports" element={<Protected roles={["owner", "manager"]} module="Reports" managerReportsGate><Reports /></Protected>} />
      <Route path="/reports/custom/print-view" element={<CustomReportPrintView />} />
      <Route path="/payments-setup" element={<Protected roles={["owner"]} module="WiPay Setup" feature="wipay"><WiPaySettings /></Protected>} />
      <Route path="/wipay-reconciliation" element={<Protected roles={["owner", "manager"]} module="WiPay Reconciliation" feature="wipay"><WiPayReconciliation /></Protected>} />
      <Route path="/promotions" element={<Protected roles={["owner", "manager"]} module="Special Promotions" feature="special_promotions"><Promotions /></Protected>} />
      <Route path="/import" element={<Protected roles={["owner", "manager"]} module="Import Data"><ImportData /></Protected>} />
      <Route path="/shift" element={<Protected><ShiftReport /></Protected>} />
      <Route path="/quotations" element={<Protected feature="quotations" module="Quotations"><Quotations /></Protected>} />
      <Route path="/sales-history" element={<Protected><SalesHistory /></Protected>} />
      <Route path="/offline-sync" element={<Protected roles={["owner", "manager"]}><OfflineSync /></Protected>} />
      <Route path="/pickups" element={<Protected><Pickups /></Protected>} />
      <Route path="/audit" element={<Protected roles={["owner", "manager"]} module="Audit Log"><AuditLog /></Protected>} />
      <Route path="/approvals" element={<Protected roles={["owner", "manager"]} module="Approvals"><ApprovalsReport /></Protected>} />
      <Route path="/staff" element={<Protected roles={["owner"]} module="Staff & Roles"><Staff /></Protected>} />
      <Route path="/settings" element={<Protected roles={["owner", "manager"]} module="Business Settings"><Settings /></Protected>} />
      <Route path="/billing" element={<Protected roles={["owner"]} module="Subscription"><Billing /></Protected>} />
      <Route path="/help" element={<Protected><Help /></Protected>} />
      <Route path="/security" element={<Protected><Security /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
